import React, { createContext, useContext, useState, useEffect } from 'react';
import { paperAPI } from '../services/api';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Load favorites on mount
  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      setIsLoading(true);
      const response = await paperAPI.getFavorites();
      const favoriteIds = new Set(response.data.map(paper => paper.id));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addToFavorites = async (paperId) => {
    try {
      await paperAPI.addToFavorites(paperId);
      setFavorites(prev => new Set([...prev, paperId]));
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      return false;
    }
  };

  const removeFromFavorites = async (paperId) => {
    try {
      await paperAPI.removeFromFavorites(paperId);
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        newFavorites.delete(paperId);
        return newFavorites;
      });
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      return false;
    }
  };

  const isFavorited = (paperId) => {
    return favorites.has(paperId);
  };

  const toggleFavorite = async (paperId) => {
    if (isFavorited(paperId)) {
      return await removeFromFavorites(paperId);
    } else {
      return await addToFavorites(paperId);
    }
  };

  const value = {
    favorites,
    isLoading,
    isFavorited,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    loadFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};